import type { SceneId, SceneTransition } from './scene.types';

const ORDER: SceneId[] = ['hall', 'alimentation', 'friandises', 'comptoir'];

/** Salles que l'on rejoint en marchant, pas en poussant une porte. */
const EN_ENFILADE: SceneId[] = ['comptoir', 'reserve', 'galerie'];

/**
 * Le passage détermine la transition : porte vers le Hall, marche le long des
 * réserves, glissement entre deux rayons voisins.
 */
export function transitionFor(from: SceneId | null, to: SceneId): SceneTransition {
  if (from === null) return 'depth-fade';
  if (EN_ENFILADE.includes(from) && EN_ENFILADE.includes(to)) return 'walk';
  if (to === 'hall' || from === 'hall') return 'door';
  const delta = ORDER.indexOf(to) - ORDER.indexOf(from);
  return delta === 0 ? 'depth-fade' : 'slide';
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** Durées cibles 350-650 ms, ramenées à presque zéro en mouvement réduit. */
export function durationFor(transition: SceneTransition): number {
  if (prefersReducedMotion()) return 1;
  switch (transition) {
    case 'slide':
      return 380;
    case 'door':
      return 620;
    case 'depth-fade':
      return 450;
    case 'walk':
      return 560;
  }
}

export function playTransition(
  element: HTMLElement,
  transition: SceneTransition,
  direction: 'out' | 'in',
): Promise<void> {
  const duration = durationFor(transition);
  element.dataset['transition'] = transition;
  element.dataset['transitionDirection'] = direction;

  const sombre = transition === 'door' || transition === 'walk';
  const voile = sombre ? 'brightness(0.72)' : 'none';

  const animation = element.animate(
    direction === 'in'
      ? [
          { opacity: 0, transform: enterFrom(transition), filter: voile },
          { opacity: 1, transform: 'none', filter: 'none' },
        ]
      : [
          { opacity: 1, transform: 'none', filter: 'none' },
          { opacity: 0, transform: leaveTo(transition), filter: voile },
        ],
    { duration, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'both' },
  );

  return animation.finished.then(() => {
    animation.cancel();
  });
}

function enterFrom(transition: SceneTransition): string {
  switch (transition) {
    case 'slide':
      return 'translateX(4%)';
    case 'door':
      return 'scale(1.04)';
    case 'depth-fade':
      return 'scale(0.985)';
    case 'walk':
      // On arrive par la droite, comme si l'on continuait à longer le rayon.
      return 'translateX(16%) scale(1.02)';
  }
}

function leaveTo(transition: SceneTransition): string {
  switch (transition) {
    case 'slide':
      return 'translateX(-4%)';
    case 'door':
      return 'scale(0.97)';
    case 'depth-fade':
      return 'scale(1.015)';
    case 'walk':
      return 'translateX(-14%) scale(0.99)';
  }
}
